import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Modal from '../Modal';
import { useConfirm } from '../ConfirmDialog';
import { downloadCsv, downloadXlsx } from '../../lib/exportTable';
import './Gdpr.css';
import '../announcements/Announcements.css';

/**
 * GDPR tools (migration 122), three pages of Settings -> Firm & system:
 *
 *   PersonalDataPage   Personal data requests: a subject access export of
 *                      a person - their own data (the default) or complete,
 *                      for review - a ZIP built in the background,
 *                      downloadable by whoever asked as often as needed for
 *                      7 days; and erasure by anonymisation - a preview of
 *                      what will change and where the first name alone
 *                      stays, the person's name typed to confirm, the run.
 *   RetentionPage      every retention rule: the ones already running, and
 *                      the five new ones, each off until switched on - and
 *                      the staff privacy notice they depend on.
 *   ProcessingPage     the record of processing (Article 30): activities and
 *                      processors, drafts until reviewed, printable and
 *                      exportable; what Jarvis holds, by category; the record.
 *
 * Administrators only (Settings is), behind the 'gdpr' feature switch.
 * Before 122 each page says which migration it needs; with the switch off,
 * where to switch it on.
 */
const API = process.env.REACT_APP_API_URL;
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;
const day = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const when = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const KIND = { staff: 'Staff', contractor: 'Contractor', guest: 'Guest' };
const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * Why a page cannot be used: needs 122, or switched off. `null` when it can.
 * Answers an element for the page to show instead of itself.
 */
function Blocked({ problem }) {
  if (!problem) return null;
  if (problem.kind === 'migration') {
    return (
      <p className="stn-banner is-warn" role="note" data-gdpr-blocked="migration">
        The GDPR tools are not set up on this server yet: they need migration 122 (<code>npm run migrate:122</code>).
      </p>
    );
  }
  if (problem.kind === 'off') {
    return (
      <p className="stn-banner is-warn" role="note" data-gdpr-blocked="off">
        The GDPR tools are switched off. Switch them on under{' '}
        <Link to="/settings/people/feature-access#feature-gdpr">Feature access</Link>.
      </p>
    );
  }
  return <p className="stn-banner is-problem" role="alert">{problem.text}</p>;
}

const problemOf = (e, fallback) => {
  const s = e?.response?.status;
  if (s === 503 && e?.response?.data?.missing === 'gdpr') return { kind: 'migration' };
  if (s === 403 && e?.response?.data?.switched_off) return { kind: 'off' };
  return { kind: 'error', text: errorOf(e, fallback) };
};

/* ============================================================ the picker */

/**
 * Find a person: staff and contractors by name or address, guests by
 * address. Only the latest answer is shown (a request counter), so a slow
 * answer to "ri" never replaces the one to "rita".
 */
function PersonPicker({ id, label, onPick, picked }) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState(null);
  const seq = useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (!term) { setFound(null); return undefined; }
    const n = ++seq.current;
    const t = setTimeout(() => {
      axios.get(`${API}/gdpr/people`, { params: { q: term } })
        .then((r) => { if (n === seq.current) setFound(r.data); })
        .catch(() => { if (n === seq.current) setFound({ staff: [], contractors: [], guests: [] }); });
    }, 200);
    return () => clearTimeout(t);
  }, [q]);
  const rows = found ? [
    ...found.staff.map((p) => ({ kind: 'staff', ref: p.id, name: p.name, email: p.email, note: p.erased_at ? 'erased' : p.active === false ? 'deactivated' : 'active' })),
    ...found.contractors.map((p) => ({ kind: 'contractor', ref: p.id, name: p.name, email: p.email, note: p.erased_at ? 'erased' : p.active ? 'active' : 'deactivated' })),
    ...found.guests.map((p) => ({ kind: 'guest', ref: p.email, name: p.name, email: p.email, note: plural(p.meetings, 'meeting') }))
  ] : [];
  return (
    <div className="gdpr-picker">
      <div className="stn-field">
        <label htmlFor={id}>{label}</label>
        <input id={id} type="search" autoComplete="off" placeholder="Name or email address" value={q}
               onChange={(e) => setQ(e.target.value)} />
      </div>
      {found && (
        <ul className="gdpr-found" aria-label="People found">
          {rows.length === 0 && <li className="stn-quiet">Nobody called that.</li>}
          {rows.map((p) => {
            const on = picked && picked.kind === p.kind && picked.ref === p.ref;
            return (
              <li key={`${p.kind}:${p.ref}`}>
                <button type="button" className={`gdpr-person${on ? ' is-on' : ''}`} aria-pressed={on}
                        data-person={`${p.kind}:${p.email}`} onClick={() => { onPick(p); }}>
                  <span className="stn-pill">{KIND[p.kind]}</span>
                  <span className="gdpr-person-name">{p.name || p.email}</span>
                  <small>{p.email}{p.note ? ` · ${p.note}` : ''}</small>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ================================================== personal data requests */

const EXPORT_STATE = {
  queued: 'Waiting', building: 'Being made', ready: 'Ready', failed: 'Failed', downloaded: 'Downloaded', expired: 'Expired'
};
const SCOPE = { own: 'Their own data', complete: 'Complete, for review' };
const times = (n) => (n === 1 ? 'once' : n === 2 ? 'twice' : `${n} times`);

function ExportCard({ onBlocked }) {
  const [picked, setPicked] = useState(null);
  const [scope, setScope] = useState('own');
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [said, setSaid] = useState('');

  const load = useCallback(() => axios.get(`${API}/gdpr/exports`)
    .then((r) => setList(r.data?.exports || [])).catch((e) => onBlocked(problemOf(e, 'Could not load the exports.'))), [onBlocked]);
  useEffect(() => { load(); }, [load]);
  // While one is being made, look again every few seconds.
  const working = (list || []).some((x) => x.state === 'queued' || x.state === 'building');
  useEffect(() => {
    if (!working) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [working, load]);

  const ask = async () => {
    if (!picked) return;
    setBusy(true); setProblem(''); setSaid('');
    try {
      await axios.post(`${API}/gdpr/exports`, { kind: picked.kind, ref: picked.ref, scope });
      setSaid(`Making the export of ${picked.name || picked.email} (${SCOPE[scope].toLowerCase()}). It appears below when it is ready.`);
      await load();
    } catch (e) { setProblem(errorOf(e, 'Could not ask for the export.')); } finally { setBusy(false); }
  };
  const fetchZip = async (x) => {
    setProblem('');
    try {
      const r = await axios.get(`${API}/gdpr/exports/${x.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = x.file_name || 'jarvis-export.zip';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setSaid(`Downloaded. You can download it again until ${day(x.expires_at)}; then it is deleted.`);
    } catch (e) {
      let msg = 'Could not download the export.';
      try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch { /* not JSON */ }
      setProblem(msg);
    } finally { load(); }
  };

  return (
    <section className="stn-card" id="s-export" aria-labelledby="gdpr-export-t">
      <h3 className="stn-card-title" id="gdpr-export-t">Subject access export</h3>
      <p className="stn-help">
        What Jarvis holds about one person, as a ZIP: a README in plain English, the data by category
        (JSON, and CSV for Excel) and their files. It is made in the background, and only you can download
        it - as often as you need, for 7 days; then it is deleted. Check it for other people&rsquo;s data
        before you send it.
      </p>
      <PersonPicker id="gdpr-export-q" label="Whose data" picked={picked} onPick={setPicked} />
      <fieldset className="gdpr-scope" data-gdpr-scope>
        <legend>Which kind</legend>
        <label className="gdpr-scope-opt">
          <input type="radio" name="gdpr-scope" value="own" checked={scope === 'own'} onChange={() => setScope('own')} />
          <span><strong>Their own data</strong> <small className="stn-quiet">(usual)</small><br />
            <small>What is about them, or that they wrote, own or uploaded: their profile, tickets and comments, their own
              chat messages (not other people&rsquo;s around them), to-dos, hours, sign-ins, files. Meant to be sent with little redaction.</small></span>
        </label>
        <label className="gdpr-scope-opt">
          <input type="radio" name="gdpr-scope" value="complete" checked={scope === 'complete'} onChange={() => setScope('complete')} />
          <span><strong>Complete, for review</strong><br />
            <small>Also every row that mentions them, shared conversations with other people&rsquo;s messages, whole
              transcripts and the recordings&rsquo; audio. Holds other people&rsquo;s data: redact before sending.</small></span>
        </label>
      </fieldset>
      <div className="stn-actions">
        <button type="button" className="btn btn-primary" disabled={!picked || busy} onClick={ask} data-gdpr-export>
          {picked ? `Make an export of ${picked.name || picked.email}` : 'Make an export'}
        </button>
      </div>
      {problem && <p className="stn-row-problem" role="alert">{problem}</p>}
      <p className="stn-flash" role="status" aria-live="polite">{said}</p>
      {list && list.length > 0 && (
        <div className="stn-table-wrap gdpr-gap">
          <table className="stn-table" aria-label="Exports">
            <thead><tr><th>About</th><th>Asked</th><th>State</th><th>Expires</th><th><span className="stn-sr">Download</span></th></tr></thead>
            <tbody>
              {list.map((x) => (
                <tr key={x.id} data-export={x.subject_label} data-state={x.state} data-scope={x.scope || 'own'}>
                  <td>
                    <span className="stn-person">{x.subject_label}</span> <small className="stn-quiet">{KIND[x.subject_kind]}</small>
                    <br /><small className={x.scope === 'complete' ? 'gdpr-complete' : 'stn-quiet'}>{SCOPE[x.scope || 'own']}</small>
                  </td>
                  <td>{day(x.requested_at)}<br /><small className="stn-quiet">{x.requested_by_name || 'Jarvis'}</small></td>
                  <td>
                    {EXPORT_STATE[x.state] || x.state}
                    {x.state === 'ready' && x.byte_size ? <small className="stn-quiet"> · {kb(x.byte_size)}</small> : null}
                    {x.download_count > 0 && (
                      <><br /><small className="stn-quiet" data-downloads>Downloaded {times(x.download_count)}, last {when(x.last_downloaded_at)}</small></>
                    )}
                    {x.error && <small className="stn-row-problem">{x.error}</small>}
                  </td>
                  <td>{['ready', 'queued', 'building'].includes(x.state) ? day(x.expires_at) : ''}</td>
                  <td>
                    {x.state === 'ready' && x.mine && (
                      <button type="button" className="btn btn-secondary btn-small" onClick={() => fetchZip(x)}>Download</button>
                    )}
                    {x.state === 'ready' && !x.mine && <small className="stn-quiet">Only {x.requested_by_name || 'who asked'} can download it</small>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PreviewTable({ preview }) {
  if (!preview?.categories?.length) return <p className="stn-quiet">Jarvis holds nothing else about them.</p>;
  return (
    <div className="stn-table-wrap">
      <table className="stn-table gdpr-preview" aria-label="What will change">
        <thead><tr><th>What</th><th>Deleted</th><th>Kept, anonymised</th><th>How</th></tr></thead>
        <tbody>
          {preview.categories.map((c) => (
            <React.Fragment key={c.id}>
              <tr className="gdpr-cat"><th colSpan={4} scope="colgroup">{c.label}</th></tr>
              {c.lines.map((l) => (
                <tr key={l.table} data-preview={l.table}>
                  <td>{l.label}</td>
                  <td className="gdpr-n">{l.deleted || ''}</td>
                  <td className="gdpr-n">{l.kept || ''}</td>
                  <td><small>{l.how}</small></td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Where the first name alone will still be after the erasure (it rewrites
 * the full name and the address only): for an administrator to read and
 * change by hand. Read-only; each place has a link to open it.
 */
function ReviewList({ review }) {
  if (!review || !review.first_name) return null;
  const name = review.first_name;
  return (
    <section className="gdpr-review" aria-labelledby="gdpr-review-t" data-gdpr-review>
      <h4 className="gdpr-sub" id="gdpr-review-t">Review by hand: the first name &lsquo;{name}&rsquo; also appears here; erasure does not change these</h4>
      {review.total === 0 ? (
        <p className="stn-quiet">&lsquo;{name}&rsquo; on its own appears nowhere else.</p>
      ) : (
        <>
          <p className="stn-help">
            {plural(review.total, 'place')} still say &lsquo;{name}&rsquo; on its own (as a whole word, in any case) once the full
            name and the address have been replaced. It may be somebody else of the same name. Open each one and change it
            yourself if it is about them.
            {review.total > review.rows.length ? ` The first ${review.rows.length} are listed.` : ''}
          </p>
          <ul className="gdpr-review-list">
            {review.rows.map((x, i) => (
              <li key={`${x.table}-${i}`} data-review={x.table}>
                <span className="stn-pill">{x.label}</span>
                {x.title && <strong> {x.title}</strong>}{' '}
                <span className="gdpr-excerpt">{x.excerpt}</span>{' '}
                {x.link && <Link to={x.link} target="_blank" rel="noopener noreferrer">Open</Link>}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

const ERASE_STATE = { queued: 'Waiting', running: 'Running', done: 'Done', failed: 'Stopped' };

function EraseCard({ onBlocked }) {
  const [picked, setPicked] = useState(null);
  const [preview, setPreview] = useState(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [said, setSaid] = useState('');
  const [list, setList] = useState(null);
  const [backups, setBackups] = useState('');
  const seq = useRef(0);

  const load = useCallback(() => axios.get(`${API}/gdpr/erasures`)
    .then((r) => { setList(r.data?.erasures || []); setBackups(r.data?.backups || ''); })
    .catch((e) => onBlocked(problemOf(e, 'Could not load the erasures.'))), [onBlocked]);
  useEffect(() => { load(); }, [load]);
  const working = (list || []).some((x) => x.state === 'queued' || x.state === 'running');
  useEffect(() => {
    if (!working) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [working, load]);

  useEffect(() => {
    setPreview(null); setTyped(''); setProblem('');
    if (!picked) return;
    const n = ++seq.current;
    axios.get(`${API}/gdpr/erasures/preview`, { params: { kind: picked.kind, ref: picked.ref } })
      .then((r) => { if (n === seq.current) setPreview(r.data); })
      .catch((e) => { if (n === seq.current) setProblem(errorOf(e, 'Could not work out what would change.')); });
  }, [picked]);

  const matches = preview && !preview.refused && typed.trim().toLowerCase() === String(preview.confirm_with || '').toLowerCase();
  const erase = async () => {
    setBusy(true); setProblem(''); setSaid('');
    try {
      const r = await axios.post(`${API}/gdpr/erasures`, { kind: picked.kind, ref: picked.ref, confirm: typed });
      setSaid(`Erasing. They become ${r.data.pseudonym}; the list below says when it is done.`);
      setPicked(null);
      await load();
    } catch (e) { setProblem(errorOf(e, 'Could not start the erasure.')); } finally { setBusy(false); }
  };
  const resume = async (x) => {
    try { await axios.post(`${API}/gdpr/erasures/${x.id}/resume`); await load(); } catch (e) { setProblem(errorOf(e, 'Could not resume it.')); }
  };

  return (
    <section className="stn-card" id="s-erase" aria-labelledby="gdpr-erase-t">
      <h3 className="stn-card-title" id="gdpr-erase-t">Erase a person</h3>
      <p className="stn-help">
        Erasing anonymises: the person becomes &ldquo;Former staff member 14&rdquo; (or contractor, or guest) everywhere.
        What is only theirs - their photo, chat files, saved filters, sign-in codes - is deleted; tickets, hours,
        invoices and licences are kept as business records with the name removed. A member of staff or a
        contractor has to be deactivated first, and an administrator removed as one.
      </p>
      <p className="stn-banner is-warn" role="note" data-gdpr-backups>
        {backups || 'Backups in R2 still hold their data until each backup ages out.'}
      </p>
      <PersonPicker id="gdpr-erase-q" label="Who" picked={picked} onPick={setPicked} />
      {picked && !preview && !problem && <p className="stn-quiet">Working out what would change…</p>}
      {preview && (
        <div className="gdpr-preview-wrap" data-gdpr-preview>
          <p className="gdpr-preview-head">
            <strong>{preview.person.label}</strong> would become <strong data-pseudonym>{preview.pseudonym}</strong>.
            {preview.totals && <> {plural(preview.totals.deleted, 'row')} deleted, {plural(preview.totals.kept, 'row')} kept and anonymised
              {preview.totals.files_deleted ? `, ${plural(preview.totals.files_deleted, 'file')} deleted` : ''}.</>}
          </p>
          {preview.refused ? (
            <div className="stn-banner is-problem" role="alert" data-gdpr-refused>
              <ul className="gdpr-reasons">{preview.refused.map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
          ) : (
            <>
              <PreviewTable preview={preview} />
              <ReviewList review={preview.review} />
              <div className="stn-field gdpr-typed">
                <label htmlFor="gdpr-typed">Type <strong>{preview.confirm_with}</strong> to confirm</label>
                <input id="gdpr-typed" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
              </div>
              <div className="stn-actions">
                <button type="button" className="btn btn-danger" disabled={!matches || busy} onClick={erase} data-gdpr-erase>
                  Erase {preview.person.name || preview.person.email}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {problem && <p className="stn-row-problem" role="alert">{problem}</p>}
      <p className="stn-flash" role="status" aria-live="polite">{said}</p>
      <h4 className="gdpr-sub" id="s-history">Erasures done</h4>
      {list && list.length === 0 && <p className="stn-quiet">Nobody has been erased.</p>}
      {list && list.length > 0 && (
        <div className="stn-table-wrap">
          <table className="stn-table" aria-label="Erasures">
            <thead><tr><th>Who they became</th><th>Confirmed</th><th>State</th><th>Changed</th><th><span className="stn-sr">Actions</span></th></tr></thead>
            <tbody>
              {list.map((x) => (
                <tr key={x.id} data-erasure={x.pseudonym} data-state={x.state}>
                  <td><span className="stn-person">{x.pseudonym}</span></td>
                  <td>{day(x.confirmed_at)}<br /><small className="stn-quiet">{x.requested_by || 'Jarvis'}</small></td>
                  <td>{ERASE_STATE[x.state] || x.state}{x.error && <small className="stn-row-problem">{x.error}</small>}</td>
                  <td>{x.totals ? <small>{plural(x.totals.deleted, 'row')} deleted, {plural(x.totals.changed, 'change')}</small> : ''}</td>
                  <td>{x.state === 'failed' && <button type="button" className="btn btn-secondary btn-small" onClick={() => resume(x)}>Resume</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function PersonalDataPage() {
  const [blocked, setBlocked] = useState(null);
  const onBlocked = useCallback((p) => setBlocked((was) => was || p), []);
  if (blocked && blocked.kind !== 'error') return <Blocked problem={blocked} />;
  return (
    <div className="gdpr">
      {blocked && <Blocked problem={blocked} />}
      <ExportCard onBlocked={onBlocked} />
      <EraseCard onBlocked={onBlocked} />
    </div>
  );
}

/* ============================================================== retention */

function RuleRow({ rule, onSaved }) {
  const [enabled, setEnabled] = useState(rule.enabled);
  const [days, setDays] = useState(String(rule.days));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  useEffect(() => { setEnabled(rule.enabled); setDays(String(rule.days)); }, [rule]);
  const dirty = enabled !== rule.enabled || Number(days) !== rule.days;
  const save = async () => {
    setBusy(true); setProblem('');
    try {
      const r = await axios.put(`${API}/gdpr/retention/${rule.key}`, { enabled, days: Number(days) });
      onSaved(r.data);
    } catch (e) { setProblem(errorOf(e, 'Could not save the rule.')); } finally { setBusy(false); }
  };
  const id = `gdpr-rule-${rule.key}`;
  return (
    <li className={`gdpr-rule${enabled ? ' is-on' : ''}`} data-rule={rule.key}>
      <div className="gdpr-rule-what">
        <label className="gdpr-switch" htmlFor={`${id}-on`}>
          <input id={`${id}-on`} type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <strong>{rule.label}</strong>
        </label>
        <small>{rule.what}</small>
        {!rule.enabled && rule.off && <small className="gdpr-rule-off" data-rule-off>{rule.off}</small>}
        <small className="stn-quiet" data-last-run>
          {rule.last_run_at ? `Last ran ${when(rule.last_run_at)}: ${plural(rule.last_rows || 0, 'row')}.` : 'Never run.'}
          {rule.updated_by ? ` Last changed by ${rule.updated_by}.` : ''}
          {rule.last_error ? ` Failed: ${rule.last_error}` : ''}
        </small>
      </div>
      <div className="gdpr-rule-days">
        <label htmlFor={`${id}-days`} className="stn-sr">Period for {rule.label}</label>
        <input id={`${id}-days`} type="number" min="1" max={rule.max || 3650} inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} />
        <span>{rule.unit}</span>
      </div>
      <div className="gdpr-rule-save">
        {dirty && <button type="button" className="btn btn-primary btn-small" disabled={busy} onClick={save}>Save</button>}
        {problem && <p className="stn-row-problem" role="alert">{problem}</p>}
      </div>
    </li>
  );
}

/*
 * THE NOTICE AS AN ANNOUNCEMENT (125): "Publish as announcement" makes the
 * saved text the next version of one Important announcement to everyone,
 * which everybody must acknowledge again - and the line under it says which
 * version is out and how many have acknowledged it, with the list. Before
 * 125 neither is offered.
 */
function NoticeAnnouncement({ status, dirty, onPublished }) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [list, setList] = useState(null);
  const [confirm, confirmElement] = useConfirm();
  if (!status || status.ready === false) return null;
  const publish = async () => {
    const ok = await confirm({
      title: status.published ? `Publish it as version ${status.version + 1}?` : 'Publish the notice as an announcement?',
      message: 'An Important announcement to everyone: a banner on every page until each person presses “I’ve read this”.'
        + (status.published ? ' Everybody acknowledges the new version, including those who acknowledged the last one.' : ''),
      confirmLabel: 'Publish'
    });
    if (!ok) return;
    setBusy(true); setProblem('');
    try {
      const r = await axios.post(`${API}/gdpr/notice/publish`);
      onPublished(r.data.status);
    } catch (e) { setProblem(errorOf(e, 'Could not publish the notice.')); }
    setBusy(false);
  };
  const open = async () => {
    try { setList((await axios.get(`${API}/gdpr/notice/acknowledgements`)).data); } catch (e) { setProblem(errorOf(e, 'Could not load the list.')); }
  };
  return (
    <div data-testid="notice-announcement">
      {status.published ? (
        <p className="ann-notice-line" data-notice-status>
          <strong>{`Staff notice v${status.version}`}</strong>
          <span>{` - acknowledged by ${status.acknowledged} of ${status.total}`}</span>
          <button type="button" className="ann-link" onClick={open}>(list)</button>
          {status.state === 'ended' && <span className="stn-quiet">Its announcement has been ended.</span>}
          {status.changed_since && <span className="stn-quiet">The text has changed since this version was published.</span>}
        </p>
      ) : <p className="ann-notice-line stn-quiet">Not published as an announcement yet.</p>}
      <div className="stn-actions">
        <button type="button" className="btn btn-plain" disabled={busy || dirty || !status.has_text} onClick={publish}
                title={dirty ? 'Save the notice first' : undefined}>
          {busy ? 'Publishing…' : 'Publish as announcement'}
        </button>
        {status.announcement_id && <Link to={`/announcements/${status.announcement_id}`}>Open the announcement</Link>}
      </div>
      {problem && <p className="stn-row-problem" role="alert">{problem}</p>}
      {list && (
        <Modal title={`Staff notice v${list.version}: who has acknowledged it`} onClose={() => setList(null)}>
          <table className="ann-table" data-testid="notice-ack-list">
            <thead><tr><th>Name</th><th>Acknowledged</th></tr></thead>
            <tbody>
              {(list.people || []).map((p) => (
                <tr key={`${p.kind}-${p.id}`} data-done={p.done ? 'yes' : 'no'}>
                  <td data-label="Name">{p.name}{p.kind === 'contractor' ? ' (contractor)' : ''}</td>
                  <td data-label="Acknowledged">{p.acknowledged_at ? when(p.acknowledged_at) : <span className="stn-quiet">Not yet</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
      {confirmElement}
    </div>
  );
}

function NoticeCard({ onSaved, announcement = null }) {
  const [notice, setNotice] = useState(null);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState('');
  const [problem, setProblem] = useState('');
  const [status, setStatus] = useState(announcement);
  useEffect(() => { setStatus(announcement); }, [announcement]);
  useEffect(() => {
    axios.get(`${API}/gdpr/notice`).then((r) => { setNotice(r.data); setBody(r.data.body || ''); }).catch(() => setNotice({ body: '' }));
  }, []);
  const save = async () => {
    setBusy(true); setSaid(''); setProblem('');
    try {
      const r = await axios.put(`${API}/gdpr/notice`, { body });
      setNotice(r.data); setSaid('Saved.'); onSaved(r.data);
      setStatus((st) => (st && st.published ? { ...st, changed_since: true, has_text: Boolean(r.data.body) } : st && { ...st, has_text: Boolean(r.data.body) }));
    } catch (e) { setProblem(errorOf(e, 'Could not save the notice.')); } finally { setBusy(false); }
  };
  return (
    <section className="stn-card" id="s-notice" aria-labelledby="gdpr-notice-t">
      <h3 className="stn-card-title" id="gdpr-notice-t">Staff privacy notice</h3>
      <p className="stn-help">
        What staff are told Jarvis records, why, who sees it and for how long. Keep the text the firm gave
        staff here, so the date it last changed sits beside the rules it has to cover.
        {notice?.updated_at ? ` Last changed ${day(notice.updated_at)}${notice.updated_by ? ` by ${notice.updated_by}` : ''}.` : ' Not written here yet.'}
      </p>
      <div className="stn-field">
        <label htmlFor="gdpr-notice-body">The notice</label>
        <textarea id="gdpr-notice-body" className="gdpr-textarea" rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
      </div>
      <div className="stn-actions">
        <button type="button" className="btn btn-primary" disabled={busy || notice === null || body === (notice?.body || '')} onClick={save}>Save the notice</button>
      </div>
      {problem && <p className="stn-row-problem" role="alert">{problem}</p>}
      <p className="stn-flash" role="status" aria-live="polite">{said}</p>
      <NoticeAnnouncement status={status} dirty={notice !== null && body !== (notice?.body || '')}
        onPublished={(st) => { setStatus(st); setSaid(`Published as version ${st.version}.`); }} />
    </section>
  );
}

export function RetentionPage() {
  const [data, setData] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const load = useCallback(() => axios.get(`${API}/gdpr/retention`)
    .then((r) => setData({ rules: [], existing: [], notice: null, ...(r.data && !Array.isArray(r.data) ? r.data : {}) }))
    .catch((e) => setBlocked(problemOf(e, 'Could not load the retention rules.'))), []);
  useEffect(() => { load(); }, [load]);
  if (blocked) return <Blocked problem={blocked} />;
  if (!data) return <p className="stn-quiet">Loading…</p>;
  const anyOn = data.rules.some((r) => r.enabled);
  return (
    <div className="gdpr">
      <p className="stn-banner is-warn" role="note" data-gdpr-notice-reminder>
        The new rules below are <strong>off until the staff notice says so</strong>: switch one on only once staff
        have been told. {data.notice?.updated_at
          ? <>The notice was last changed on {day(data.notice.updated_at)}.</>
          : <>No staff notice has been saved here yet - <a href="#notice">add it below</a>.</>}
      </p>
      <section className="stn-card" id="s-rules" aria-labelledby="gdpr-rules-t">
        <h3 className="stn-card-title" id="gdpr-rules-t">Retention rules</h3>
        <p className="stn-help">
          Each runs once a day when it is on. {anyOn ? '' : 'All are off.'} Deleting cannot be undone, and
          the backups in R2 keep a copy until they age out.
        </p>
        <ul className="gdpr-rules">
          {data.rules.map((r) => (
            <RuleRow key={r.key} rule={r} onSaved={(saved) => setData((d) => ({ ...d, rules: d.rules.map((x) => (x.key === saved.key ? saved : x)) }))} />
          ))}
        </ul>
      </section>
      <section className="stn-card" id="s-existing" aria-labelledby="gdpr-existing-t">
        <h3 className="stn-card-title" id="gdpr-existing-t">What is already deleted, and when</h3>
        <p className="stn-help">These run as they always have. Where there is a setting, change it where it lives.</p>
        <div className="stn-table-wrap">
          <table className="stn-table" aria-label="Retention already running">
            <thead><tr><th>What</th><th>Kept for</th><th>Last ran</th><th><span className="stn-sr">Setting</span></th></tr></thead>
            <tbody>
              {data.existing.map((x) => (
                <tr key={x.key} data-existing={x.key}>
                  <td>{x.label}</td>
                  <td>{x.rule}</td>
                  <td>{x.last_run_at ? when(x.last_run_at) : <span className="stn-quiet">{x.job ? 'Not yet' : 'As things happen'}</span>}</td>
                  <td>{x.setting && <Link to={x.setting}>Change</Link>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <NoticeCard announcement={data.notice?.announcement || null}
        onSaved={(n) => setData((d) => ({ ...d, notice: { ...(d.notice || {}), updated_at: n.updated_at, has_text: Boolean(n.body) } }))} />
    </div>
  );
}

/* =================================================== record of processing */

const FIELDS = [
  ['name', 'Name', false],
  ['purposes', 'Purposes', true],
  ['data_categories', 'Categories of personal data', true],
  ['data_subjects', 'Whose data', false],
  ['lawful_basis', 'Lawful basis', true],
  ['recipients', 'Recipients', true],
  ['transfers', 'Transfers outside the EU', true],
  ['retention', 'Retention', true],
  ['safeguards', 'Safeguards', true]
];

function EntryForm({ entry, kind, onClose, onSaved }) {
  // `stored`: the words as kept, where the register shows more than that
  // while a setting is on (every chat call recorded, 4 Nov) - the form
  // edits what is kept, so the live sentence is never saved into it.
  const [v, setV] = useState(() => Object.fromEntries(FIELDS.map(([k]) => [k, (entry?.stored && k in entry.stored ? entry.stored[k] : entry?.[k]) || ''])));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const save = async () => {
    setBusy(true); setProblem('');
    try {
      if (entry) await axios.patch(`${API}/gdpr/register/${entry.id}`, v);
      else await axios.post(`${API}/gdpr/register`, { ...v, kind });
      onSaved();
    } catch (e) { setProblem(errorOf(e, 'Could not save it.')); } finally { setBusy(false); }
  };
  return (
    <Modal title={entry ? `Change ${entry.name}` : kind === 'processor' ? 'Add a processor' : 'Add a processing activity'} onClose={onClose} wide
      footer={(
        <>
          {problem && <p className="stn-row-problem" role="alert">{problem}</p>}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy || !v.name.trim()} onClick={save}>Save</button>
        </>
      )}>
      <div className="gdpr-form">
        {FIELDS.map(([k, label, long]) => (
          <div className="stn-field" key={k}>
            <label htmlFor={`gdpr-f-${k}`}>{label}</label>
            {long
              ? <textarea id={`gdpr-f-${k}`} className="gdpr-textarea" rows={3} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
              : <input id={`gdpr-f-${k}`} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} />}
          </div>
        ))}
      </div>
    </Modal>
  );
}

function EntryCard({ x, onEdit, onReview, onRemove }) {
  return (
    <li className="gdpr-entry" data-entry={x.name}>
      <div className="gdpr-entry-head">
        <h4>{x.name}</h4>
        <span className={`stn-pill gdpr-status${x.status === 'reviewed' ? ' is-reviewed' : ''}`} data-status={x.status}>
          {x.status === 'reviewed' ? `Reviewed ${day(x.reviewed_at)}` : 'Draft — review'}
        </span>
      </div>
      {x.live === 'recording_chat_calls' && (
        <p className="tds-quiet gdpr-live" data-live="recording_chat_calls">
          Every chat call is recorded for quality control (Settings → Recording &amp; AI), so this entry says so for as long as it is on.
        </p>
      )}
      <dl className="gdpr-dl">
        {FIELDS.slice(1).filter(([k]) => x[k]).map(([k, label]) => (
          <React.Fragment key={k}><dt>{label}</dt><dd>{x[k]}</dd></React.Fragment>
        ))}
      </dl>
      <div className="stn-row-actions gdpr-noprint">
        <button type="button" className="btn btn-secondary btn-small" onClick={() => onEdit(x)}>Change</button>
        <button type="button" className="btn btn-secondary btn-small" onClick={() => onReview(x)}>
          {x.status === 'reviewed' ? 'Back to draft' : 'Mark reviewed'}
        </button>
        <button type="button" className="btn btn-danger-outline btn-small" onClick={() => onRemove(x)}>Remove</button>
      </div>
    </li>
  );
}

export function ProcessingPage() {
  const [reg, setReg] = useState(null);
  const [holds, setHolds] = useState(null);
  const [record, setRecord] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [editing, setEditing] = useState(null);   // { entry?, kind }
  const [problem, setProblem] = useState('');
  const [confirm, confirmElement] = useConfirm();

  const load = useCallback(() => Promise.all([
    axios.get(`${API}/gdpr/register`).then((r) => setReg({ activities: [], processors: [], ...(r.data && !Array.isArray(r.data) ? r.data : {}) })),
    axios.get(`${API}/gdpr/registry`).then((r) => setHolds(r.data)),
    axios.get(`${API}/gdpr/record`, { params: { limit: 50 } }).then((r) => setRecord(r.data))
  ]).catch((e) => setBlocked(problemOf(e, 'Could not load the record of processing.'))), []);
  useEffect(() => { load(); }, [load]);

  // Printing: only the register, via a class on <body> (Gdpr.css @media print).
  useEffect(() => {
    const done = () => document.body.classList.remove('gdpr-printing');
    window.addEventListener('afterprint', done);
    return () => { window.removeEventListener('afterprint', done); done(); };
  }, []);
  const print = () => { document.body.classList.add('gdpr-printing'); window.print(); };
  const exportAs = async (how) => {
    setProblem('');
    try {
      const r = await axios.get(`${API}/gdpr/register/export`);
      if (how === 'csv') downloadCsv(r.data.columns, r.data.rows, r.data.name);
      else downloadXlsx(r.data.columns, r.data.rows, r.data.name, 'Record of processing');
    } catch (e) { setProblem(errorOf(e, 'Could not export the register.')); }
  };
  const review = async (x) => {
    try { await axios.patch(`${API}/gdpr/register/${x.id}`, { status: x.status === 'reviewed' ? 'draft' : 'reviewed' }); load(); } catch (e) { setProblem(errorOf(e, 'Could not change it.')); }
  };
  const remove = async (x) => {
    const ok = await confirm({ title: `Remove ${x.name}?`, message: 'It goes from the record of processing. This is recorded.', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try { await axios.delete(`${API}/gdpr/register/${x.id}`); load(); } catch (e) { setProblem(errorOf(e, 'Could not remove it.')); }
  };

  const drafts = useMemo(() => (reg ? [...reg.activities, ...reg.processors].filter((x) => x.status !== 'reviewed').length : 0), [reg]);
  if (blocked) return <Blocked problem={blocked} />;
  if (!reg) return <p className="stn-quiet">Loading…</p>;
  const section = (kind, id, title, lead, rows) => (
    <section className="stn-card" id={`s-${id}`} aria-labelledby={`gdpr-${id}-t`}>
      <div className="gdpr-section-head">
        <h3 className="stn-card-title" id={`gdpr-${id}-t`}>{title}</h3>
        <button type="button" className="btn btn-secondary btn-small gdpr-noprint" onClick={() => setEditing({ kind })}>
          {kind === 'processor' ? 'Add a processor' : 'Add an activity'}
        </button>
      </div>
      <p className="stn-help">{lead}</p>
      <ul className="gdpr-entries">
        {rows.map((x) => <EntryCard key={x.id} x={x} onEdit={(e) => setEditing({ entry: e, kind })} onReview={review} onRemove={remove} />)}
      </ul>
    </section>
  );
  return (
    <div className="gdpr">
      <div className="gdpr-toolbar gdpr-noprint">
        <button type="button" className="btn btn-secondary btn-small" onClick={print}>Print or save as PDF</button>
        <button type="button" className="btn btn-secondary btn-small" onClick={() => exportAs('csv')}>CSV</button>
        <button type="button" className="btn btn-secondary btn-small" onClick={() => exportAs('xlsx')}>Excel</button>
      </div>
      {problem && <p className="stn-banner is-problem" role="alert">{problem}</p>}
      <div className="gdpr-print">
        <h2 className="gdpr-print-title">Record of processing activities — Jarvis (NCMB)</h2>
        {drafts > 0 && (
          <p className="stn-banner is-warn" role="note" data-gdpr-drafts>
            {plural(drafts, 'entry is a draft', 'entries are drafts')} written from what Jarvis knows about itself. Review each one -
            the lawful basis and the transfers especially - before relying on it. This is not legal advice.
          </p>
        )}
        {section('activity', 'activities', 'Processing activities', 'What Jarvis does with personal data, and why.', reg.activities)}
        {section('processor', 'processors', 'Processors', 'Who Jarvis sends personal data to, to do it.', reg.processors)}
      </div>
      <section className="stn-card gdpr-noprint" id="s-holds" aria-labelledby="gdpr-holds-t">
        <h3 className="stn-card-title" id="gdpr-holds-t">What Jarvis holds about people</h3>
        <p className="stn-help">From the personal-data registry the export and the erasure are built from: every place a person&rsquo;s data is kept, and what erasing them does to it.</p>
        {(holds?.categories || []).map((c) => (
          <details key={c.id} className="gdpr-holds" data-category={c.id}>
            <summary><strong>{c.label}</strong> <small className="stn-quiet">{c.what}</small></summary>
            <ul>
              {c.tables.map((t) => (
                <li key={t.table}><strong>{t.label}</strong> <small className="stn-quiet">({t.whose.join(', ')})</small>
                  <br /><small>Erasure: {t.erase}{t.retention ? ` Retention: ${t.retention}` : ''}</small></li>
              ))}
            </ul>
          </details>
        ))}
      </section>
      <section className="stn-card gdpr-noprint" id="s-record" aria-labelledby="gdpr-record-t">
        <h3 className="stn-card-title" id="gdpr-record-t">Data-protection record</h3>
        <p className="stn-help">Every export, erasure, retention change and register change, newest first.</p>
        {record?.entries?.length ? (
          <ul className="gdpr-record">
            {record.entries.map((x) => (
              <li key={x.id}><span className="stn-quiet">{when(x.at)}</span> <strong>{x.actor_name}</strong>{x.on_behalf_by ? ` (as ${x.on_behalf_by})` : ''}: {x.said}</li>
            ))}
          </ul>
        ) : <p className="stn-quiet">Nothing yet.</p>}
      </section>
      {editing && <EntryForm entry={editing.entry} kind={editing.kind} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {confirmElement}
    </div>
  );
}
